import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Calendar,
  FileText,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  Mail,
  Send,
  Loader2,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { auth } from '@/config/firebase';
import { toast } from 'react-hot-toast';
import UserInfoModal, { UserInfo } from '@/components/sb-search-term-report/UserInfoModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import {
  fetchSBSearchTermData,
  SBSearchTermRecord,
  SearchFilters
} from '@/services/sbSearchTermService';

interface ReportStats {
  totalReports: number;
  lastReportDate: Date | null;
  totalSearchTerms: number;
  totalSpend: number;
  totalSales: number;
  totalImpressions: number;
  totalClicks: number;
  averageAcos: number;
}

const SBSearchTermReportDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<ReportStats>({
    totalReports: 0,
    lastReportDate: null,
    totalSearchTerms: 0,
    totalSpend: 0,
    totalSales: 0,
    totalImpressions: 0,
    totalClicks: 0,
    averageAcos: 0
  });
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'pending'>('disconnected');

  // Send Authentication Link state (legacy - keeping for backward compatibility)
  const [clientEmail, setClientEmail] = useState('');
  const [clientFirstName, setClientFirstName] = useState('');
  const [sendingLink, setSendingLink] = useState(false);

  // New modal flow state
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);

  // Data table state
  const [tableData, setTableData] = useState<SBSearchTermRecord[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [companyName, setCompanyName] = useState('test-company'); // TODO: Get from auth/context
  const [filters, setFilters] = useState<SearchFilters>({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    // Load connection status and stats from Firestore
    loadDashboardData();
    
    // Check URL parameters for OAuth return flow
    checkOAuthReturn();
    
    // Load table data
    loadTableData();
  }, []);

  const loadTableData = async () => {
    setLoadingData(true);
    try {
      const result = await fetchSBSearchTermData(companyName, filters);
      setTableData(result.records);
      console.log(`✅ Loaded ${result.records.length} records`);
    } catch (error) {
      console.error('Error loading table data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadTableData();
  };

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement Firestore queries to load dashboard data
      // For now, using mock data
      setStats({
        totalReports: 0,
        lastReportDate: null,
        totalSearchTerms: 0,
        totalSpend: 0,
        totalSales: 0,
        totalImpressions: 0,
        totalClicks: 0,
        averageAcos: 0
      });
      setConnectionStatus('disconnected');
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkOAuthReturn = async () => {
    // OAuth callback handling is now done in index.tsx routing
    // This function is kept for any other callback handling if needed
  };

  const handleConnectAmazon = () => {
    setShowUserInfoModal(true);
  };

  const handleUserInfoSubmit = async (userInfo: UserInfo) => {
    try {
      setIsLoading(true);
      
      // Store user info in session storage for OAuth return flow
      sessionStorage.setItem('amazonAuth_userInfo', JSON.stringify(userInfo));
      
      // Call the initiateAmazonAuth function
      const initiateAmazonAuth = httpsCallable(functions, 'initiateAmazonAuth');
      const result = await initiateAmazonAuth({
        userInfo: {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          companyName: userInfo.companyName
        },
        originUrl: window.location.origin
      });
      
      const data = result.data as any;
      if (data.authorizationUrl) {
        // Redirect to Amazon OAuth
        window.location.href = data.authorizationUrl;
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (error: any) {
      console.error('Error initiating Amazon auth:', error);
      toast.error(error.message || 'Failed to initiate Amazon authentication');
    } finally {
      setIsLoading(false);
      setShowUserInfoModal(false);
    }
  };

  const handleCloseUserInfoModal = () => {
    setShowUserInfoModal(false);
  };

  const handleGenerateReport = async () => {
    // TODO: Implement report generation
    setIsLoading(true);
    try {
      console.log('Generating report...');
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAuthLink = async () => {
    if (!clientEmail.trim() || !clientFirstName.trim()) {
      toast.error('Please enter both client email and first name');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setSendingLink(true);
      
      // Get the current user's ID token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }
      
      const idToken = await user.getIdToken();
      
      const response = await fetch('https://us-central1-jls-apps.cloudfunctions.net/sendAmazonAuthenticationLink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          clientEmail: clientEmail.trim(),
          clientFirstName: clientFirstName.trim()
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send authentication link');
      }
      
      toast.success(`Authentication link sent successfully to ${clientEmail}!`);
      
      // Clear the form
      setClientEmail('');
      setClientFirstName('');
    } catch (error: any) {
      console.error('Failed to send authentication link:', error);
      toast.error(error.message || 'Failed to send authentication link');
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SB Search Term Report</h1>
          <p className="text-muted-foreground mt-1">
            Analyze your Amazon Sponsored Brands search term performance
          </p>
        </div>
        <div className="flex gap-3">
          {connectionStatus === 'connected' ? (
            <Button 
              onClick={handleGenerateReport}
              disabled={isLoading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleConnectAmazon}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Connect Amazon Account
            </Button>
          )}
        </div>
      </div>

      {/* Connection Status Alert & Send Auth Link */}
      {connectionStatus === 'disconnected' && (
        <div className="space-y-6">
          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle>Amazon Account Not Connected</AlertTitle>
            <AlertDescription>
              Send an authentication link to your client so they can connect their Amazon Advertising account.
            </AlertDescription>
          </Alert>

          {/* Send Authentication Link Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-amber-600" />
                Send Authentication Link
              </CardTitle>
              <CardDescription>
                Send a secure authentication link to your client so they can connect their Amazon Advertising account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientFirstName">Client First Name</Label>
                  <Input
                    id="clientFirstName"
                    type="text"
                    placeholder="Enter client's first name"
                    value={clientFirstName}
                    onChange={(e) => setClientFirstName(e.target.value)}
                    disabled={sendingLink}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientEmail">Client Email Address</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    placeholder="client@example.com"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    disabled={sendingLink}
                  />
                </div>
              </div>
              <Button 
                onClick={handleSendAuthLink}
                disabled={sendingLink || !clientEmail.trim() || !clientFirstName.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                {sendingLink ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending Link...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Authentication Link
                  </>
                )}
              </Button>
              <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                <strong>What happens next:</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>Your client receives a secure email with a 24-hour authentication link</li>
                  <li>They click the link and log into their Amazon Advertising account</li>
                  <li>They select which advertising profile to connect</li>
                  <li>Reports will automatically generate daily at 2AM EST</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {connectionStatus === 'connected' && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle>Amazon Account Connected</AlertTitle>
          <AlertDescription>
            Your Amazon Advertising account is connected and ready to generate reports.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalReports}</div>
            <p className="text-xs text-muted-foreground">
              {stats.lastReportDate
                ? `Last: ${format(stats.lastReportDate, 'MMM dd, yyyy')}`
                : 'No reports yet'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Search Terms</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalSearchTerms.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">Unique terms analyzed</p>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${stats.totalSpend.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">Across all campaigns</p>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average ACoS</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.averageAcos.toFixed(2)}%
            </div>
            <p className="text-xs text-muted-foreground">Advertising cost of sales</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different report views */}
      <Tabs defaultValue="data" className="space-y-4">
        <TabsList>
          <TabsTrigger value="data">Data View</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="recent">Recent Reports</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Search Term Data</CardTitle>
                  <CardDescription>
                    View and filter your Sponsored Brands search term performance
                  </CardDescription>
                </div>
                <Button
                  onClick={handleApplyFilters}
                  disabled={loadingData}
                  variant="outline"
                  size="sm"
                >
                  {loadingData ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="campaignName">Campaign Name</Label>
                  <Input
                    id="campaignName"
                    type="text"
                    placeholder="Filter by campaign..."
                    value={filters.campaignName || ''}
                    onChange={(e) => handleFilterChange('campaignName', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="searchTerm">Search Term</Label>
                  <Input
                    id="searchTerm"
                    type="text"
                    placeholder="Filter by search term..."
                    value={filters.searchTerm || ''}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  />
                </div>
              </div>

              {/* Data Table */}
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-left font-medium">Campaign Name</th>
                      <th className="px-4 py-3 text-left font-medium">Ad Group Name</th>
                      <th className="px-4 py-3 text-left font-medium">Targeting</th>
                      <th className="px-4 py-3 text-left font-medium">Match Type</th>
                      <th className="px-4 py-3 text-left font-medium">Customer Search Term</th>
                      <th className="px-4 py-3 text-right font-medium">Impressions</th>
                      <th className="px-4 py-3 text-right font-medium">Viewable Impr.</th>
                      <th className="px-4 py-3 text-right font-medium">Clicks</th>
                      <th className="px-4 py-3 text-right font-medium">Spend</th>
                      <th className="px-4 py-3 text-right font-medium">14 Day Sales</th>
                      <th className="px-4 py-3 text-right font-medium">14 Day Orders</th>
                      <th className="px-4 py-3 text-right font-medium">14 Day Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingData ? (
                      <tr>
                        <td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading data...
                        </td>
                      </tr>
                    ) : tableData.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">
                          No data found for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      tableData.map((record, index) => (
                        <tr
                          key={index}
                          className="border-t hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">{record.date}</td>
                          <td className="px-4 py-3">{record.campaignName}</td>
                          <td className="px-4 py-3">{record.adGroupName}</td>
                          <td className="px-4 py-3">{record.targeting}</td>
                          <td className="px-4 py-3">{record.matchType}</td>
                          <td className="px-4 py-3">{record.customerSearchTerm}</td>
                          <td className="px-4 py-3 text-right">{record.impressions.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{record.viewableImpressions.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{record.clicks.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">${record.spend.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">${record.sales14d.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{record.orders14d}</td>
                          <td className="px-4 py-3 text-right">{record.units14d}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Record Count */}
              {!loadingData && tableData.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Showing {tableData.length} record{tableData.length !== 1 ? 's' : ''}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
                <CardDescription>
                  Key performance indicators from your search term reports
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Impressions</span>
                    <span className="font-medium">{stats.totalImpressions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Clicks</span>
                    <span className="font-medium">{stats.totalClicks.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Click-Through Rate</span>
                    <span className="font-medium">
                      {stats.totalImpressions > 0 
                        ? ((stats.totalClicks / stats.totalImpressions) * 100).toFixed(2)
                        : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Sales</span>
                    <span className="font-medium">${stats.totalSales.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>
                  Common tasks and settings for your reports
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Calendar className="mr-2 h-4 w-4" />
                  Schedule Daily Reports
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Download className="mr-2 h-4 w-4" />
                  Export All Data
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Users className="mr-2 h-4 w-4" />
                  Manage Amazon Profiles
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="recent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Reports</CardTitle>
              <CardDescription>
                Your most recently generated search term reports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                No reports generated yet. Connect your Amazon account to get started.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Reports</CardTitle>
              <CardDescription>
                Automated report generation settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Daily Reports</AlertTitle>
                  <AlertDescription>
                    Reports are automatically generated daily at 2:00 AM EST for all connected profiles.
                  </AlertDescription>
                </Alert>
                <div className="text-sm text-muted-foreground">
                  Connect your Amazon account to enable automatic report generation.
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <UserInfoModal
        open={showUserInfoModal}
        onOpenChange={setShowUserInfoModal}
        onSubmit={handleUserInfoSubmit}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SBSearchTermReportDashboard;